import styled from '@emotion/styled'
import { CreateSite } from '@pubstack/common/src/site'
import { FunctionComponent, useEffect } from 'react'
import { FieldErrors, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useCreateSites } from '~/api/app/sites.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { Flyout } from '~/components/Flyout'
import { Input } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'

const FlyoutActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 24px;
`

const AddSiteForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 26px;

  & > section {
    padding-right: 64px;
    display: inline-flex;
    gap: 20px;
    & > * {
      flex-grow: 1;
    }

    & > button {
      /** TODO nra tmu 2022-10-18 this is used instead of a aling-items: center because of alert messages moving everything */
      margin-top: 4px;
    }
  }
`

type PureSettingsSitesAddSiteFlyoutProps = {
  validateSite: (siteName: string | boolean | CreateSite | CreateSite[] | undefined) => boolean
}
export const SettingsSitesAddSiteFlyout: FunctionComponent<PureSettingsSitesAddSiteFlyoutProps> = ({ validateSite }) => {
  const user = useAuthenticatedUser()
  const flyout = useGlobalFlyout()
  const { mutateAsync: addSites } = useCreateSites(user.currentScopeId, { errorToast: 'An error occurred while trying to update your inventory.' })
  const {
    control,
    reset,
    handleSubmit,
    getValues,
    setError,
    trigger,
    formState: { errors, isValid, isDirty },
  } = useForm<{ sites: CreateSite[] }>({
    defaultValues: { sites: [{ name: '', env: 'web' }] },
    mode: 'onChange',
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sites',
  })

  const watchedSites = useWatch({ control, name: 'sites' })

  // Keep a trailing empty input: when the last site gains a name, append a fresh one.
  useEffect(() => {
    const sites = watchedSites ?? []
    if (sites[sites.length - 1]?.name) {
      append({ name: '', env: 'web' })
    }
    // Re-validate every name field on each edit so a duplicate is flagged (and cleared) on both
    // inputs, not just the one currently being typed in.
    trigger()
  }, [watchedSites, append, trigger])

  const onSubmit = async (values: { sites: CreateSite[] }) => {
    try {
      await addSites(values.sites.filter((s) => s.name.trim().length).map((s) => ({ name: s.name.trim(), env: s.env })))
      flyout.close()
    } catch (error: any) {
      if (error?.message === 'Conflict data' && error?.description?.resourceIds) {
        // Handle multiple conflicts
        const conflictNames = error.description.resourceIds
        const currentSites = getValues('sites')

        conflictNames.forEach((siteName: string) => {
          const index = currentSites.findIndex((s) => s.name.trim() === siteName)
          if (index !== -1) {
            setError(`sites.${index}.name`, { type: 'duplicate' })
          }
        })
      }
    }
  }

  const getErrorMessage = (errors: FieldErrors<{ sites: CreateSite[] }>, index: number) => {
    if (errors?.sites?.[index]?.name?.type === 'required') {
      return 'At least one site name is required'
    }
    if (errors?.sites?.[index]?.name?.type === 'pattern') {
      return 'A name cannot only be whitespaces'
    }
    if (errors?.sites?.[index]?.name?.type === 'unique') {
      return 'This name is already used in another field. Please choose another.'
    }
    if (errors?.sites?.[index]?.name?.type === 'alreadyExists' || errors?.sites?.[index]?.name?.type === 'duplicate') {
      return 'This name is already in use. Please choose another.'
    }
  }

  return (
    <Flyout.Content>
      <Flyout.Title>Add site</Flyout.Title>
      <Flyout.Body>
        <AddSiteForm>
          {fields.map((field, index) => (
            <section key={field.id}>
              <Input
                error={getErrorMessage(errors, index)}
                rules={{
                  required: fields.length - 1 !== index,
                  pattern: /.*[^ ].*/,
                  validate: {
                    alreadyExists: validateSite,
                    unique: (value, formValues) => {
                      const name = typeof value === 'string' ? value.trim().toLocaleLowerCase() : ''
                      if (!name) return true
                      return formValues.sites.filter((site) => site.name.trim().toLocaleLowerCase() === name).length <= 1
                    },
                  },
                }}
                control={control}
                name={`sites.${index}.name`}
                label={'Site name'}
              />
              {fields.length > 1 && (
                <Button
                  disabled={fields.length - 1 === index}
                  onClick={() => {
                    remove(index)
                  }}
                  variant={'tertiary'}
                  iconName={'delete'}
                  trackingLabel={'common-settings-sites-creation-item-delete'}
                />
              )}
            </section>
          ))}
        </AddSiteForm>
      </Flyout.Body>
      <Flyout.Actions>
        <FlyoutActions>
          {isDirty && (
            <Button iconName={'restore'} onClick={() => reset()} variant={'tertiary'} trackingLabel={'common-settings-sites-creation-restore'}>
              Reset
            </Button>
          )}
          <Button disabled={!isValid || fields.length === 1} onClick={handleSubmit(onSubmit)} trackingLabel={'common-settings-sites-creation-save'}>
            Add site
          </Button>
        </FlyoutActions>
      </Flyout.Actions>
    </Flyout.Content>
  )
}
